import React, {useCallback, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {getFontFamily, getFontSize, useDidMount, useDidUpdate} from 'utils'
import {GIFT_SHOP_HEADER_HEIGHT} from 'consts'
import {useWindowLayout} from 'windows'
import convertUnit from 'lib/unit'
import {Button, Image, Paragraph} from 'common/components'

interface ImageProps {
  height: number
}

const StyledContainer = styled.div`
  width: 100%;
  flex-direction: column;
  justify-content: space-between;
  text-align: center;
`
const StyledImageContainer = styled.div<ImageProps>`
  box-sizing: border-box;
  height: ${({height}) => convertUnit(height)};
`
const StyledImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  box-sizing: border-box;
`
const StyledBottomContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: ${convertUnit(20)};
  margin: auto;
`
const StyledParagraph = styled(Paragraph)`
  text-align: center;
`
const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
  width: fill-available;
  margin-top: ${convertUnit(20)};
  min-height: 20%;
`
const StyledButton = styled(Button)`
  width: 100%;
`
const StyledOl = styled.ol`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  margin: 0;
  padding-left: ${convertUnit(20)};
  padding-top: ${convertUnit(8)};
`
const StyledLi = styled.li`
  font-family: ${getFontFamily('medium')};
  font-size: ${getFontSize('s')};
  color: ${({theme}) => theme.danger_5};
  text-align: left;
`

interface GiftShopEditProfileSelfieConfirmationProps {
  selfie: string
  onRetry(): void
  onAccept(): void
  isFailed: boolean
}

export default function GiftShopEditProfileSelfieConfirmation({
  selfie,
  onAccept,
  onRetry,
  isFailed,
}: GiftShopEditProfileSelfieConfirmationProps) {
  const {translate} = useTranslation()
  const {width} = useWindowLayout()
  const [bottomHeight, setBottomHeight] = useState<number>()

  const renderWarningItem = useCallback(
    (item: string) => <StyledLi>{item}</StyledLi>,
    [],
  )

  useDidUpdate(() => {
    setBottomHeight(document.getElementById('bottomContainer')?.offsetHeight)
  }, [width, isFailed])

  useDidMount(() => {
    setBottomHeight(document.getElementById('bottomContainer')?.offsetHeight)
  })

  return (
    <StyledContainer>
      {bottomHeight && (
        <StyledImageContainer
          height={window.innerHeight - GIFT_SHOP_HEADER_HEIGHT - bottomHeight}>
          <StyledImage src={selfie} alt="" />
        </StyledImageContainer>
      )}
      <StyledBottomContainer id="bottomContainer">
        {isFailed ? (
          <>
            <StyledParagraph fontSize="m">
              {translate('giftShop:editCredentialSelfieWarningTitle')}
            </StyledParagraph>
            <StyledOl>
              {Array.from({length: 3}).map((_, idx) =>
                renderWarningItem(
                  translate('giftShop:kycSelfieWarning', {
                    context: `${idx + 1}`,
                  }),
                ),
              )}
            </StyledOl>
          </>
        ) : (
          <>
            <StyledParagraph fontSize="m">
              {translate('giftShop:kycSelfieReviewTitle')}
            </StyledParagraph>
            <StyledParagraph fontWeight="medium" color="danger_5">
              {translate('giftShop:kycSelfieReviewMessage')}
            </StyledParagraph>
          </>
        )}
        <StyledButtonContainer>
          {isFailed ? null : (
            <StyledButton
              backgroundColor="white_3"
              color="primary_5"
              label={translate('giftShop:kycSelfie')}
              onClick={onRetry}
            />
          )}
          <StyledButton
            label={translate(isFailed ? 'global:retry' : 'global:yes')}
            onClick={isFailed ? onRetry : onAccept}
          />
        </StyledButtonContainer>
      </StyledBottomContainer>
    </StyledContainer>
  )
}
